import { AppSwitch } from "../../common/AppSwitch";
import { useEffect, useMemo, useState } from "react";
import { View } from "react-native";
import { Search } from "lucide-react-native";
import {
  JobPreferencePill,
  jobPreferenceStyles as styles,
} from "../../jobs/JobPreferencePill";
import { Text, TextInput } from "@/components/common/AppText";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../hooks/useTheme";
import { useLocale } from "../../../hooks/useLocale";
import { PHONE_CODES } from "../../../lib/country-data";
import { localizedCountryName } from "../../../lib/country-names";
import type { ComponentProps } from "react";
import type { CVJobPreferences } from "../../../lib/cv-mock-data";

/** Preserve commas/decimal text while typing, and still reflect hydration and resets. */
function DraftInput({
  modelValue,
  formatValue,
  parseValue,
  onValueChange,
  ...props
}: Omit<
  ComponentProps<typeof TextInput>,
  "value" | "defaultValue" | "onChangeText"
> & {
  modelValue: unknown;
  formatValue: string;
  parseValue: (text: string) => unknown;
  onValueChange: (text: string) => void;
}) {
  const [text, setText] = useState(formatValue);
  useEffect(() => {
    setText((current) =>
      JSON.stringify(parseValue(current)) === JSON.stringify(modelValue)
        ? current
        : formatValue,
    );
  }, [modelValue, formatValue, parseValue]);
  return (
    <TextInput
      {...props}
      value={text}
      onChangeText={(next) => {
        setText(next);
        onValueChange(next);
      }}
    />
  );
}
const parseList = (text: string) =>
  text
    .split(/[,،]/)
    .map((item) => item.trim())
    .filter(Boolean);
const parseSalary = (text: string) => (text.trim() ? Number(text) : null);

export function JobSearchConstraintsFields({
  value,
  onChange,
  part = "all",
}: {
  value: CVJobPreferences;
  onChange: (value: CVJobPreferences) => void;
  /**
   * Render only the country picker or only the narrowing filters (cities,
   * exclusions, seniority, salary), so a form can keep countries up front and
   * tuck the rest away. "all" (both, with outer margin) is the native layout.
   */
  part?: "all" | "countries" | "filters";
}) {
  const { theme } = useTheme();
  const { t } = useTranslation("seeker");
  const { locale, isRTL } = useLocale();
  const [search, setSearch] = useState("");
  const label = (code: string) =>
    localizedCountryName(
      code,
      locale,
      PHONE_CODES.find((country) => country.code === code)?.name ?? code,
    );
  const countries = useMemo(() => value.countries ?? [], [value.countries]);
  const options = useMemo(
    () =>
      PHONE_CODES.filter(
        (country) =>
          !countries.includes(country.code) &&
          (country.name.toLowerCase().includes(search.toLowerCase()) ||
            localizedCountryName(country.code, locale, country.name).includes(
              search,
            )),
      ).slice(0, 8),
    [search, countries, locale],
  );
  const fieldStyle = [
    styles.input,
    {
      color: theme.text,
      textAlign: isRTL ? ("right" as const) : ("left" as const),
      backgroundColor: theme.surfaceSecondary,
    },
  ];
  const labelStyle = [styles.label, { color: theme.text }];
  const helpStyle = [styles.help, { color: theme.textSecondary }];
  const patch = (next: Partial<CVJobPreferences>) =>
    onChange({ ...value, ...next });
  const chooseCountries = (next: string[]) =>
    patch({
      countries: next,
      preferredLocations: [],
      searchAreas: [],
      cities: [],
    });
  const chips = (
    values: string[],
    selected: string[],
    onSelect: (value: string) => void,
    names?: (value: string) => string,
    exclusive = false,
  ) => (
    <View style={styles.row}>
      {values.map((item) => (
        <JobPreferencePill
          key={item}
          label={names?.(item) ?? item}
          selected={selected.includes(item)}
          onPress={() => onSelect(item)}
          exclusive={exclusive}
        />
      ))}
    </View>
  );
  const showCountries = part !== "filters";
  const showFilters = part !== "countries";
  return (
    <View
      style={{
        gap: 24,
        marginVertical: part === "all" ? 12 : 0,
        direction: isRTL ? "rtl" : "ltr",
      }}
    >
      {showCountries && (
        <View style={styles.section}>
          <Text style={labelStyle}>{t("jobMatching.countries")}</Text>
          <Text style={helpStyle}>{t("jobMatching.countryHelp")}</Text>
          <JobPreferencePill
            label={t("jobMatching.mainCvCountry")}
            selected={countries.length === 0}
            onPress={() => chooseCountries([])}
            exclusive
          />
          {countries.length > 0 &&
            chips(
              countries,
              countries,
              (code) =>
                chooseCountries(countries.filter((item) => item !== code)),
              label,
            )}
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 10,
              borderRadius: 28,
              paddingHorizontal: 20,
              backgroundColor: theme.surfaceSecondary,
            }}
          >
            <Search size={20} color={theme.textTertiary} strokeWidth={1.5} />
            <TextInput
              accessibilityLabel={t("jobMatching.searchCountries")}
              placeholder={t("jobMatching.searchCountries")}
              placeholderTextColor={theme.textTertiary}
              value={search}
              onChangeText={setSearch}
              autoCorrect={false}
              style={[
                styles.input,
                {
                  flex: 1,
                  minWidth: 0,
                  paddingHorizontal: 0,
                  color: theme.text,
                  textAlign: isRTL ? "right" : "left",
                },
              ]}
            />
          </View>
          {chips(
            options.map((country) => country.code),
            countries,
            (code) => {
              chooseCountries([...countries, code]);
              setSearch("");
            },
            label,
          )}
          {options.length === 0 && search.trim() !== "" && (
            <Text style={helpStyle}>{t("jobMatching.noCountries")}</Text>
          )}
          <Text style={helpStyle}>{t("jobMatching.relocationHelp")}</Text>
        </View>
      )}
      {showFilters &&
        (["cities", "excludedRoles", "excludedCompanies"] as const).map(
          (field) => (
            <View key={field} style={styles.section}>
              <Text style={labelStyle}>{t(`jobMatching.${field}`)}</Text>
              <DraftInput
                accessibilityLabel={t(`jobMatching.${field}`)}
                modelValue={value[field] ?? []}
                formatValue={(value[field] ?? []).join(", ")}
                parseValue={parseList}
                onValueChange={(text) => patch({ [field]: parseList(text) })}
                placeholder={t("jobMatching.commaSeparated")}
                placeholderTextColor={theme.textTertiary}
                style={fieldStyle}
              />
            </View>
          ),
        )}
      {showFilters && (
        <>
          <View style={styles.section}>
            <Text style={labelStyle}>{t("jobMatching.seniority")}</Text>
            <Text style={helpStyle}>{t("jobMatching.allLevels")}</Text>
            {chips(
              [
                "entry",
                "mid",
                "senior",
                "lead",
                "manager",
                "director",
                "executive",
                "internship",
              ],
              value.seniorityLevels ?? [],
              (level) =>
                patch({
                  seniorityLevels: value.seniorityLevels?.includes(level)
                    ? value.seniorityLevels.filter((item) => item !== level)
                    : [...(value.seniorityLevels ?? []), level],
                }),
              (level) => t(`jobMatching.levels.${level}`),
            )}
          </View>
          <View style={styles.section}>
            <Text style={labelStyle}>{t("jobMatching.minimumSalary")}</Text>
            <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
              <DraftInput
                accessibilityLabel={t("jobMatching.minimumSalary")}
                modelValue={value.minimumSalary ?? null}
                formatValue={value.minimumSalary?.toString() ?? ""}
                parseValue={parseSalary}
                keyboardType="decimal-pad"
                placeholder={t("jobMatching.salaryAmount")}
                placeholderTextColor={theme.textTertiary}
                onValueChange={(text) =>
                  patch({
                    minimumSalary: parseSalary(text),
                    salaryCurrency: value.salaryCurrency ?? "AED",
                    salaryPeriod: value.salaryPeriod ?? "monthly",
                  })
                }
                style={[fieldStyle, { flexGrow: 1, flexBasis: 180 }]}
              />
              <TextInput
                accessibilityLabel={t("jobMatching.currency")}
                placeholder={t("jobMatching.currency")}
                placeholderTextColor={theme.textTertiary}
                value={value.salaryCurrency ?? "AED"}
                autoCapitalize="characters"
                autoCorrect={false}
                maxLength={3}
                onChangeText={(text) =>
                  patch({ salaryCurrency: text.toUpperCase() })
                }
                style={[fieldStyle, { flexGrow: 1, flexBasis: 90 }]}
              />
            </View>
            {chips(
              ["monthly", "yearly"],
              [value.salaryPeriod ?? "monthly"],
              (period) =>
                patch({ salaryPeriod: period as "monthly" | "yearly" }),
              (period) => t(`jobMatching.${period}`),
              true,
            )}
            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
              }}
            >
              <Text style={[helpStyle, { flex: 1 }]}>
                {t("jobMatching.unknownSalary")}
              </Text>
              <AppSwitch
                accessibilityLabel={t("jobMatching.unknownSalary")}
                value={value.includeUnknownSalary !== false}
                onValueChange={(includeUnknownSalary) =>
                  patch({ includeUnknownSalary })
                }
              />
            </View>
          </View>
        </>
      )}
    </View>
  );
}
